"use client";

import { useState } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import { toWebp } from "@/utils/common";

import "swiper/css";

const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";

interface Props {
  testimonials: any[];
  swiperRef: React.MutableRefObject<SwiperType | null>;
  onSlideChange: (index: number) => void;
}

const LOOP_ADDITIONAL_SLIDES = 8;

const breakpoints = {
  640: { slidesPerView: 1 },
  768: { slidesPerView: 2.34 },
  991: { slidesPerView: 2.34},
  1280: { slidesPerView: 2.34 },
  1440: { slidesPerView: 2.34},
  1600: { slidesPerView: 2.8},
};

// Swiper's loop mode needs slides.length >= slidesPerView + slidesPerGroup + loopAdditionalSlides,
// otherwise it silently breaks and navigation freezes on the last real slide.
const minSlidesForLoop =
  Math.ceil(Math.max(...Object.values(breakpoints).map((b) => b.slidesPerView))) +
  1 +
  LOOP_ADDITIONAL_SLIDES;

const ReviewSlider = ({ testimonials, swiperRef, onSlideChange }: Props) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  if (!testimonials?.length) return null;

  const slidesToRender =
    testimonials.length >= minSlidesForLoop
      ? testimonials
      : Array.from(
          { length: minSlidesForLoop },
          (_, i) => testimonials[i % testimonials.length],
        );

  return (
    <>
      <style jsx global>{`
        .testimonial-swiper .swiper-wrapper {
          align-items: stretch;
        }
        .testimonial-swiper .swiper-slide {
          height: auto;
        }
      `}</style>
      <Swiper
        className="testimonial-swiper"
        modules={[Navigation, Autoplay]}
        spaceBetween={40}
        loop
        loopAdditionalSlides={LOOP_ADDITIONAL_SLIDES}
        autoplay={{ delay: 3000, disableOnInteraction: false }}
        breakpoints={breakpoints}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={(swiper) => {
          const realIndex = swiper.realIndex % testimonials.length;
          setCurrentSlide(realIndex);
          onSlideChange(realIndex + 1);
        }}
      >
        {slidesToRender.map((item: any, index: number) => {
          const avatarUrl = item?.image?.data?.attributes?.url;

          return (
            <SwiperSlide key={`${item?.id ?? index}-${index}`} className="h-auto">
              <div
                className="flex h-[476px] flex-col bg-white p-[40px_24px_24px_24px] "
                style={{
                  boxShadow: `
                2px 1px 5px 0px #00000005,
                8px 4px 9px 0px #00000005,
                18px 9px 12px 0px #00000003,
                33px 16px 15px 0px #00000000,
                51px 26px 16px 0px #00000000,
              `,
                }}
              >
                {/* Content */}
                <div className="flex-1">
                  {/* Project */}
                  <h4 className="xl:text-[24px] xl:leading-[31.2px] text-[20px] leading-[26px] tracking-[1px] font-bold text-black mb-[16px]">
                    Project
                  </h4>

                  <p className="xl:mb-[40px] mb-[32px] xl:text-[18px] xl:leading-[25.2px] text-[16px] leading-[22.4px] tracking-[1px] font-regular text-secondary-text line-clamp-2">
                    {item?.title}
                  </p>

                  {/* Review */}
                  <h4 className="xl:text-[24px] xl:leading-[31.2px] text-[20px] leading-[26px] tracking-[1px] font-bold text-black mb-[16px]">
                    Review
                  </h4>

                  <p className="xl:mb-[24px] mb-[30px] xl:text-[18px] xl:leading-[25.2px] text-[16px] leading-[22.4px] tracking-[1px] font-regular text-secondary-text line-clamp-5">
                    {item?.description}
                  </p>
                </div>

                {/* Reviewer */}
                <div className="mt-auto mb-[24px]">
                  <div className="flex items-center gap-[16px]">
                    <Image
                      src={
                        avatarUrl
                          ? toWebp(`${baseUrl}${avatarUrl}`)
                          : "/images/placeholder.png"
                      }
                      alt={item?.reviewer_name || "Reviewer"}
                      width={67}
                      height={67}
                      className="object-cover w-[67px] h-[67px] object-cover"
                    />

                    <div>
                      <h5 className="xl:text-[16px] x:leading-[22.4px] text-[14px] leading-[19.6px] font-bold text-black mb-[4px] ">
                        {item?.author}
                      </h5>

                      <p className="mt-1 text-[14px] leading-[19.6px] text-[#83858C] font-regular">
                        {item?.author_position}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>

      {/* Mobile Pagination */}
      <div className="mt-8 flex items-center md:justify-start justify-center gap-[6px] xl:hidden">
        {testimonials.map((_: any, index: number) => (
          <div
            key={index}
            className={`relative overflow-hidden rounded-full transition-all duration-300 ${
              currentSlide === index
                ? "h-[3px] w-[56px] bg-[#D8ECFB]"
                : "h-[3px] w-[10px] bg-[#C4C9D4]"
            }`}
          >
            {currentSlide === index && (
              <div className="h-full w-full bg-gradient-to-r from-[#8ABC00] to-[#0086E6]" />
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default ReviewSlider;
